import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { BookOpen, MapPin } from 'lucide-react';
import { SEOHead } from '@/components/seo/SEOHead';
import { guidesApi, type Guide } from '@/api/endpoints/guides';

interface GroupedGuides {
  [cityKey: string]: {
    name: string;
    state: string;
    slug: string;
    guides: Guide[];
  };
}

const TYPE_LABELS: Record<string, string> = {
  'happy-hours': 'Happy Hours',
  'events': 'Weekly Events',
  'deals': 'Food & Drink Deals',
};

const TYPE_ORDER = ['happy-hours', 'events', 'deals'];

export function GuidesIndexPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['guides-index'],
    queryFn: () => guidesApi.getAll(),
    staleTime: 1000 * 60 * 60,
  });

  const grouped: GroupedGuides = {};
  if (data?.guides) {
    for (const guide of data.guides) {
      const key = guide.locationSlug || guide.slug;
      if (!grouped[key]) {
        grouped[key] = {
          name: guide.locationName || '',
          state: guide.locationState || '',
          slug: guide.locationSlug || '',
          guides: [],
        };
      }
      grouped[key].guides.push(guide);
    }
  }

  // Sort cities by name
  const cities = Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <SEOHead
        title="City Guides"
        description="Your guide to the best happy hours, weekly events, and food & drink deals in cities across the Midwest. Written by locals, updated daily."
        canonicalPath="/guides"
      />

      <div className="mx-auto max-w-3xl px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--color-text)] mb-2">City Guides</h1>
          <p className="text-[var(--color-muted)]">
            Hand-picked happy hours, weekly events, and food & drink deals — verified against real data and updated regularly.
          </p>
        </div>

        {isLoading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-lg bg-[var(--color-card)] p-6 border border-[var(--color-border)]">
                <div className="h-5 w-40 bg-[var(--color-border)] rounded mb-3" />
                <div className="h-4 w-64 bg-[var(--color-border)] rounded" />
              </div>
            ))}
          </div>
        )}

        {!isLoading && cities.length === 0 && (
          <p className="text-[var(--color-muted)]">No guides available yet.</p>
        )}

        <div className="space-y-6">
          {cities.map((city) => (
            <div
              key={city.slug}
              className="rounded-lg bg-[var(--color-card)] border border-[var(--color-border)] overflow-hidden"
            >
              <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--color-border)]">
                <MapPin className="h-4 w-4 text-accent-primary" />
                <Link
                  to={`/city/${city.slug}`}
                  className="text-lg font-semibold text-[var(--color-text)] hover:text-accent-primary transition-colors"
                >
                  {city.name}, {city.state}
                </Link>
              </div>

              <div className="divide-y divide-[var(--color-border)]">
                {TYPE_ORDER.map((type) => {
                  const guide = city.guides.find((g) => g.guideType === type);
                  if (!guide) return null;
                  return (
                    <Link
                      key={guide.slug}
                      to={`/guide/${guide.slug}`}
                      className="flex items-start gap-3 px-5 py-3 hover:bg-[var(--color-border)]/30 transition-colors"
                    >
                      <BookOpen className="h-4 w-4 mt-0.5 shrink-0 text-accent-primary" />
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-[var(--color-text)]">
                          {TYPE_LABELS[type] || guide.title}
                        </span>
                        <p className="text-xs text-[var(--color-muted)] mt-0.5 line-clamp-1">
                          {guide.metaDescription}
                        </p>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center text-sm text-[var(--color-muted)]">
          <p>
            All guides are written using real data from{' '}
            <Link to="/" className="text-accent-primary hover:underline">
              Bar Guide
            </Link>
            . Prices and times are verified against our database of specials and events.
          </p>
        </div>
      </div>
    </>
  );
}
